/**
 * SidebarNotesTab - Notes tab content for VideoSidebar
 * Auto-saving notes with dictation support for Gutachter
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { useTranslation } from "react-i18next";
import { Button } from "@jourfx/ui";
import { Mic, Save, Check } from "lucide-react";

// Type-only alias for Convex Id type
type Id<T extends string> = string & { __tableName: T };

// Type declarations for Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  onend: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognition;
    webkitSpeechRecognition?: new () => SpeechRecognition;
  }
}

interface SidebarNotesTabProps {
  appointmentId: string;
  /** Demo mode skips Convex queries and uses local state only */
  isDemo?: boolean;
}

export function SidebarNotesTab({ appointmentId, isDemo = false }: SidebarNotesTabProps) {
  const { t } = useTranslation("video");
  const [content, setContent] = useState("");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [isDictating, setIsDictating] = useState(false);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasInitializedRef = useRef(false);

  // Convex queries and mutations - skip in demo mode
  const notesData = useQuery(
    api.video.notes.getNotes,
    isDemo ? "skip" : { appointmentId: appointmentId as Id<"appointments"> }
  );
  const updateNotes = useMutation(api.video.notes.updateNotes);

  // Check for SpeechRecognition support on mount
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = "de-DE";

      recognitionRef.current.onresult = (event: SpeechRecognitionEvent) => {
        let transcript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            transcript += result[0].transcript + " ";
          }
        }
        if (transcript) {
          setContent((prev) => prev + transcript);
        }
      };

      recognitionRef.current.onerror = () => {
        setIsDictating(false);
      };

      recognitionRef.current.onend = () => {
        setIsDictating(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Initialize content from query result
  useEffect(() => {
    if (notesData?.content !== undefined && !hasInitializedRef.current) {
      setContent(notesData.content);
      hasInitializedRef.current = true;
    }
  }, [notesData?.content]);

  // Save notes to Convex
  const saveNotes = useCallback(async (noteContent: string) => {
    setSaveStatus("saving");
    try {
      await updateNotes({
        appointmentId: appointmentId as Id<"appointments">,
        content: noteContent,
      });
      setSaveStatus("saved");
      // Reset to idle after 2 seconds
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (error) {
      console.error("Failed to save notes:", error);
      setSaveStatus("idle");
    }
  }, [appointmentId, updateNotes]);

  // Auto-save with 1000ms debounce when content changes
  useEffect(() => {
    // Skip auto-save if content hasn't been initialized yet
    if (!hasInitializedRef.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveNotes(content);
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [content, saveNotes]);

  return (
    <div className="flex flex-col h-full p-4">
      {/* Textarea */}
      <div className="relative flex-1 mb-4">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={t("notes.placeholder")}
          className="w-full h-full resize-none border border-border rounded-lg p-3 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
          aria-label={t("notes.ariaLabel")}
        />

        {/* Status indicators */}
        <div className="absolute top-2 right-2 flex items-center gap-2">
          {/* Dictation indicator */}
          {isDictating && (
            <div className="flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive"></span>
              </span>
              <span className="text-xs text-destructive font-medium">{t("notes.recording")}</span>
            </div>
          )}

          {/* Save status indicator */}
          {saveStatus === "saving" && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Save className="w-3 h-3 animate-pulse" />
              <span className="text-xs">{t("notes.saving")}</span>
            </div>
          )}
          {saveStatus === "saved" && (
            <div className="flex items-center gap-1 text-green-600">
              <Check className="w-3 h-3" />
              <span className="text-xs">{t("notes.saved")}</span>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col gap-2">
        {/* Dictation button - temporarily deactivated */}
        <Button
          variant="outline"
          disabled
          className="w-full"
          aria-label={t("notes.dictation.start")}
        >
          <Mic className="w-4 h-4 mr-2" />
          {t("notes.dictation.start")}
        </Button>

        {/* AI transcript intentionally deferred */}
      </div>
    </div>
  );
}
